import { useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Card,
  CardActionArea,
  CardContent,
  Container,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";

import iceLogo from "../../ui/images/logo/iceLogo.png";
import capxLogo from "../../ui/images/logo/capxLogo.png";
import LifeCycleCostLogo from "../../ui/images/logo/LifeCycleCostLogo.png";
import { ROUTES } from "../../constants/routesConstants";
import UploadProjectButton from "../../ui/UploadProjectButton";
import {
  EVALUATION_TYPES,
  PROJECT_KEY,
} from "../../constants/storageConstants";
import TooltipIcon from "../../ui/TooltipIcon";
import { GREY_1 } from "../../constants/styleConstants";
import FormComponent from "../../ui/FormComponent";

export default function Homepage({
  handleNewAnalysisClick,
  navigateToNextStep,
  projectInfo,
  setCurrentProject,
}) {
  const navigate = useNavigate();
  const onProjectUpload = (uploadedProjectData) => {
    setCurrentProject({
      freshProjectKey: `${PROJECT_KEY}-${Date.now()}`,
      freshProjectData: uploadedProjectData,
    });
    return navigate(ROUTES.PROJECT_INFORMATION);
  };

  const logo =
    projectInfo?.evaluationType === EVALUATION_TYPES.LIFE_CYCLE_COST
      ? { src: LifeCycleCostLogo, alt: "Life Cycle Cost Flow" }
      : { src: capxLogo, alt: "Cap-X Flow" };

  const updatedDate = new Date(projectInfo?.updatedDate).toLocaleDateString();
  const [currentTab, setCurrentTab] = useState(0);
  const getA11yProps = (index) => ({
    id: `tab-${index}`,
    "aria-controls": `tabpanel-${index}`,
  });
  const BORDER_STYLE = `2px solid ${GREY_1}`;

  return (
    <div style={{ overflowY: "auto" }}>
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          justifyContent: "space-between",
          padding: "20px",
        }}
      >
        <div>
          <Grid columnSpacing={12} sx={{ marginBottom: "50px" }} container>
            <Grid item xs={12} md={6}>
              <Typography
                sx={{
                  marginBottom: "90px",
                  marginTop: "65px",
                }}
                variant="h4"
              >
                PennDOT Web Intersection <br /> Control Evaluation (ICE) Tool
              </Typography>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ marginBottom: "5px" }} variant="h6">
                  Start analysis
                </Typography>
                <div
                  style={{
                    display: "flex",
                    marginBottom: "50px",
                  }}
                >
                  <Button
                    color="secondary"
                    style={{ marginRight: "20px" }}
                    onClick={handleNewAnalysisClick}
                    variant="contained"
                  >
                    New
                  </Button>
                  <FormComponent
                    defaultValues={{}}
                    id="uploadProjectForm"
                    onFormSubmit={() => {}}
                  >
                    {({ formData, register }) => {
                      return (
                        <div style={{ display: "flex" }}>
                          <UploadProjectButton
                            name="uploadProjectFile"
                            onUpload={onProjectUpload}
                            register={register}
                            value={formData.uploadProjectFile}
                          >
                            Upload
                          </UploadProjectButton>
                          <TooltipIcon title="Resume an existing analysis by uploading its .json file" />
                        </div>
                      );
                    }}
                  </FormComponent>
                </div>
                {!!projectInfo && (
                  <div>
                    <Typography variant="h6">Continue analysis</Typography>
                    <Card>
                      <CardActionArea onClick={navigateToNextStep}>
                        <CardContent>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <div style={{ marginRight: "10px" }}>
                              <Typography
                                gutterBottom
                                variant="h5"
                                component="div"
                              >
                                {projectInfo.projectName}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {projectInfo.analystName}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {projectInfo.agencyOrganization}
                              </Typography>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                              }}
                            >
                              <img
                                alt={logo.alt}
                                src={logo.src}
                                style={{ height: "50px", margin: "5px" }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {updatedDate}
                              </Typography>
                            </div>
                          </div>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </div>
                )}
              </div>
            </Grid>
            <Grid
              item
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
              xs={12}
              md={6}
            >
              <img
                alt="Intersection Control Evaluation (ICE)"
                src={iceLogo}
                style={{
                  marginBottom: "20px",
                  marginTop: "70px",
                  maxWidth: "170px",
                }}
              />
              <div
                style={{
                  height: "60px",
                  width: "1px",
                  borderRight: BORDER_STYLE,
                }}
              ></div>
              <div
                style={{
                  height: "5px",
                  width: "240px",
                  borderTop: BORDER_STYLE,
                  borderLeft: BORDER_STYLE,
                  borderRight: BORDER_STYLE,
                  marginBottom: "20px",
                }}
              ></div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-around",
                  marginBottom: "50px",
                }}
              >
                <img
                  alt="CAP-X Capacity Analysis for Planning of Junctions"
                  src={capxLogo}
                  style={{ marginRight: "100px", maxHeight: "100px" }}
                />
                <img
                  alt="Life Cycle Cost Analysis"
                  src={LifeCycleCostLogo}
                  style={{ maxHeight: "100px" }}
                />
              </div>
              <Tabs
                aria-label="tabs for updating detailed demand profiles"
                onChange={(event, newTab) => setCurrentTab(newTab)}
                sx={{
                  borderBottom: "1px solid #B1B1B1",
                  marginBottom: "30px",
                  marginTop: "40px",
                  width: "100%",
                }}
                value={currentTab}
              >
                <Tab
                  label="CAP-X"
                  sx={{ minWidth: "50%" }}
                  {...getA11yProps(0)}
                />
                <Tab
                  label="LIfe Cycle Cost"
                  sx={{ minWidth: "50%" }}
                  {...getA11yProps(1)}
                />
              </Tabs>
              <div
                style={{
                  minHeight: "250px",
                  textAlign: "justify",
                  wordSpacing: "-1px",
                }}
              >
                {currentTab === 0 && (
                  <Typography style={{ marginBottom: "30px" }} variant="body1">
                    The Federal Highway Administration (FHWA) Office of
                    Operations Research and Development developed the first
                    version of Capacity Analysis for Planning of Junctions
                    (CAP-X) in 2009. The PennDOT web ICE Tool contains a
                    near-replication of the most recent FHWA Excel version of
                    CAP-X. CAP-X is a tool that evaluates intersections and
                    interchanges at the planning level using the method of
                    critical lane volume summation to provide planning capacity
                    assessment at each crossing. It is designed to be simple,
                    with only movement volume counts and number of lanes needed
                    to be entered into the tool.
                  </Typography>
                )}
                {currentTab === 1 && (
                  <>
                    <Typography
                      style={{ marginBottom: "30px" }}
                      variant="body1"
                    >
                      This web tool provides life cycle cost comparisons between
                      different intersection treatments. The tool incorporates
                      the following costs:
                    </Typography>
                    <ul>
                      <li>Safety</li>
                      <li>Vehicular delay</li>
                      <li>Operations and maintenance</li>
                      <li>Design, construction, and right-of-way</li>
                    </ul>
                  </>
                )}
              </div>
            </Grid>
          </Grid>
        </div>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Link style={{ marginRight: "20px" }} to={ROUTES.ABOUT}>
            About
          </Link>
          <Link style={{ marginRight: "20px" }} to={ROUTES.CONTACT}>
            Contact
          </Link>
          <Link to={ROUTES.DISCLAIMER}>Disclaimer</Link>
        </div>
      </Container>
    </div>
  );
}

Homepage.propTypes = {
  handleNewAnalysisClick: PropTypes.func.isRequired,
  navigateToNextStep: PropTypes.func.isRequired,
  projectInfo: PropTypes.shape({
    projectName: PropTypes.string.isRequired,
    analystName: PropTypes.string.isRequired,
  }),
  setCurrentProject: PropTypes.func.isRequired,
};
